import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Bot,
  Send,
  Loader2,
  Sparkles,
  AlertTriangle,
  Calendar,
  DollarSign,
  Users,
  Briefcase,
  CheckCircle,
  XCircle,
  ChevronRight,
  RefreshCw,
  Zap,
  Shield,
} from 'lucide-react';
import { formatTime } from '../lib/utils';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const TACO_MASCOT_URL = "https://customer-assets.emergentagent.com/job_ttouchmanage/artifacts/sua7mu0g_Untitled%20design%20%288%29.png";

/**
 * Taco v2 Hybrid Dashboard Panel
 * Features: KPIs + Alerts + Quick Actions + Chat
 */
const TacoV2Panel = () => {
  const { api, user, isAdmin, isStaff } = useAuth();
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [v2Enabled, setV2Enabled] = useState(false);
  
  // Phase 3: Proactive alerts
  const [proactiveAlerts, setProactiveAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [resolvingAlert, setResolvingAlert] = useState(null);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Check if v2 is enabled and load dashboard
  useEffect(() => {
    checkV2Status();
  }, []);

  const checkV2Status = async () => {
    try {
      const healthRes = await api().get('/taco/v2/health');
      setV2Enabled(healthRes.data.v2_enabled);
      
      if (healthRes.data.v2_enabled) {
        await loadDashboard();
        await loadProactiveAlerts();
      }
    } catch (error) {
      console.error('Taco v2 health check failed:', error);
      setV2Enabled(false);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    try {
      const res = await api().get('/taco/v2/dashboard');
      setDashboard(res.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load Taco dashboard');
    }
  };
  
  // Phase 3: Load proactive intelligence alerts
  const loadProactiveAlerts = async () => {
    setLoadingAlerts(true);
    try {
      const res = await api().get('/taco/v2/alerts?limit=10');
      setProactiveAlerts(res.data.alerts || []);
    } catch (error) {
      console.error('Failed to load proactive alerts:', error);
    } finally {
      setLoadingAlerts(false);
    }
  };
  
  // Phase 3: Resolve alert
  const resolveAlert = async (alertId) => {
    setResolvingAlert(alertId);
    try {
      await api().post(`/taco/v2/alerts/${alertId}/resolve`, {
        resolution_note: 'Resolved via dashboard'
      });
      toast.success('Alert resolved');
      // Remove from local state
      setProactiveAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (error) {
      console.error('Failed to resolve alert:', error);
      toast.error('Failed to resolve alert');
    } finally {
      setResolvingAlert(null);
    }
  };
  
  // Phase 4: Execute recommended action
  const [executingAction, setExecutingAction] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  
  const executeAction = async (alertId, action) => {
    setExecutingAction(`${alertId}_${action.action_id}`);
    try {
      const res = await api().post(`/taco/v2/alerts/${alertId}/execute_action`, {
        action: action.action_id,
        confirm: true
      });
      
      if (res.data.success) {
        toast.success(`Action "${action.label}" executed successfully`);
        
        // If alert was auto-resolved, remove from list
        if (res.data.alert_resolved) {
          setProactiveAlerts(prev => prev.filter(a => a.id !== alertId));
        } else {
          // Refresh alerts to get updated state
          await loadProactiveAlerts();
        }
        
        // Refresh dashboard
        await loadDashboard();
      } else {
        toast.error(res.data.error || 'Action failed');
      }
    } catch (error) {
      console.error('Failed to execute action:', error);
      toast.error(error.response?.data?.detail || 'Failed to execute action');
    } finally {
      setExecutingAction(null);
      setConfirmModal(null);
    }
  };
  
  const openConfirmModal = (alertId, action) => {
    setConfirmModal({ alertId, action });
  };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Send message to Taco v2 chat endpoint
  const sendMessage = async (messageText = null) => {
    const text = messageText || inputMessage.trim();
    if (!text) return;

    // Add user message to UI
    const userMsg = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setSending(true);

    try {
      const res = await api().post('/taco/v2/chat', {
        message: text,
        conversation_id: conversationId
      });

      // Add assistant response
      const assistantMsg = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: res.data.message,
        type: res.data.type,
        tool_calls: res.data.tool_calls || [],
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMsg]);

      // Set conversation ID if not set
      if (!conversationId) {
        setConversationId(`conv_${Date.now()}`);
      }

      // Refresh dashboard if tools were executed
      if (res.data.tool_calls?.length > 0) {
        await loadDashboard();
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg = {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: error.response?.data?.detail || 'Sorry, I encountered an error. Please try again.',
        type: 'error',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleQuickAction = (prompt) => {
    sendMessage(prompt);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!v2Enabled) {
    return (
      <div className="p-6 text-center">
        <Bot className="h-16 w-16 text-zinc-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-zinc-700">Taco v2 Not Enabled</h2>
        <p className="text-zinc-500 mt-2">The operational AI engine is not active.</p>
        <p className="text-sm text-zinc-400 mt-1">Set TACO_V2_ENABLED=true to activate.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col lg:flex-row gap-4 p-4">
      {/* Left Panel: Dashboard */}
      <div className="lg:w-1/3 space-y-4">
        {/* User Info */}
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <img 
                src={TACO_MASCOT_URL} 
                alt="Taco" 
                className="w-12 h-12 rounded-full bg-amber-100"
                onError={(e) => e.target.style.display = 'none'}
              />
              <div>
                <h2 className="font-semibold text-zinc-900">Hey, {user?.full_name?.split(' ')[0]}!</h2>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs capitalize">
                    {user?.role}
                  </Badge>
                  <span className="text-xs text-zinc-500">
                    {dashboard?.capabilities_count || 0} tools available
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPIs */}
        {dashboard?.kpis?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-500" />
                Quick Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              {dashboard.kpis.map((kpi) => (
                <div 
                  key={kpi.id}
                  className={cn(
                    "p-3 rounded-lg border",
                    kpi.severity === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-zinc-50 border-zinc-200'
                  )}
                >
                  <p className="text-2xl font-bold text-zinc-900">{kpi.value}</p>
                  <p className="text-xs text-zinc-500">{kpi.label}</p>
                  {kpi.subtext && (
                    <p className="text-xs text-zinc-400 mt-1">{kpi.subtext}</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Phase 3 & 4: Proactive Intelligence Alerts with Actions */}
        {proactiveAlerts.length > 0 && (
          <Card className="border-red-200" data-testid="proactive-alerts-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                  <AlertTriangle className="h-4 w-4" />
                  Proactive Alerts ({proactiveAlerts.length})
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={loadProactiveAlerts}
                  disabled={loadingAlerts}
                  className="h-6 px-2"
                  data-testid="refresh-alerts-btn"
                >
                  <RefreshCw className={cn("h-3 w-3", loadingAlerts && "animate-spin")} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {proactiveAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={cn(
                    "p-3 rounded-lg border transition-all",
                    alert.severity === 'high' ? 'bg-red-50 border-red-200' :
                    alert.severity === 'medium' ? 'bg-amber-50 border-amber-200' :
                    'bg-zinc-50 border-zinc-200'
                  )}
                  data-testid={`alert-${alert.id}`}
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className={cn(
                      "h-4 w-4 flex-shrink-0 mt-0.5",
                      alert.severity === 'high' ? 'text-red-500' :
                      alert.severity === 'medium' ? 'text-amber-500' :
                      'text-zinc-400'
                    )} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge 
                          variant="outline" 
                          className={cn(
                            "text-xs px-1.5 py-0",
                            alert.severity === 'high' ? 'border-red-300 text-red-700' :
                            alert.severity === 'medium' ? 'border-amber-300 text-amber-700' :
                            'border-zinc-300'
                          )}
                        >
                          {alert.type.replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-xs text-zinc-400">
                          {formatTime(alert.detected_at)}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-700">{alert.message}</p>
                      {alert.related_booking_id && (
                        <p className="text-xs text-zinc-500 mt-1">
                          Booking: {alert.related_booking_id.slice(0, 8)}...
                        </p>
                      )}
                      
                      {/* Phase 4: Recommended Actions */}
                      {alert.recommended_actions?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-zinc-200/50">
                          <p className="text-xs text-zinc-500 mb-1.5">Recommended Actions:</p>
                          <div className="flex flex-wrap gap-1.5">
                            {alert.recommended_actions.map((action) => (
                              <Button
                                key={action.action_id}
                                variant="outline"
                                size="sm"
                                onClick={() => openConfirmModal(alert.id, action)}
                                disabled={executingAction === `${alert.id}_${action.action_id}`}
                                className={cn(
                                  "h-6 px-2 text-xs",
                                  action.auto_resolve ? 'border-green-300 text-green-700 hover:bg-green-50' : ''
                                )}
                                data-testid={`action-${alert.id}-${action.action_id}`}
                              >
                                {executingAction === `${alert.id}_${action.action_id}` ? (
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                  <Zap className="h-3 w-3 mr-1" />
                                )}
                                {action.label}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => resolveAlert(alert.id)}
                      disabled={resolvingAlert === alert.id}
                      className="h-7 px-2 text-xs flex-shrink-0"
                      data-testid={`resolve-alert-${alert.id}`}
                    >
                      {resolvingAlert === alert.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Resolve
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        {/* Legacy context alerts (fallback) */}
        {proactiveAlerts.length === 0 && dashboard?.alerts?.length > 0 && (
          <Card className="border-amber-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-4 w-4" />
                Alerts ({dashboard.alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {dashboard.alerts.map((alert, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickAction(`Tell me about the ${alert.type} alert`)}
                  className={cn(
                    "w-full text-left p-2 rounded-lg flex items-center gap-2 hover:opacity-80 transition-opacity",
                    alert.severity === 'high' ? 'bg-red-50 text-red-800' :
                    alert.severity === 'medium' ? 'bg-amber-50 text-amber-800' :
                    'bg-zinc-50 text-zinc-800'
                  )}
                >
                  <AlertTriangle className={cn(
                    "h-4 w-4 flex-shrink-0",
                    alert.severity === 'high' ? 'text-red-500' :
                    alert.severity === 'medium' ? 'text-amber-500' :
                    'text-zinc-400'
                  )} />
                  <span className="text-sm flex-1">{alert.message}</span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-50" />
                </button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {dashboard?.quick_actions?.map((action) => (
              <Button
                key={action.id}
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => handleQuickAction(action.prompt)}
                data-testid={`quick-action-${action.id}`}
              >
                {action.label}
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* Refresh */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={loadDashboard}
          className="w-full text-zinc-500"
        >
          <RefreshCw className="h-3 w-3 mr-2" />
          Refresh Dashboard
        </Button>
      </div>

      {/* Right Panel: Chat */}
      <div className="lg:w-2/3 flex flex-col">
        <Card className="flex-1 flex flex-col min-h-[500px]">
          <CardHeader className="pb-2 border-b">
            <CardTitle className="text-sm flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-amber-500" />
                Chat with Taco
              </div>
              <Badge variant="secondary" className="text-xs">
                v2 Operational
              </Badge>
            </CardTitle>
          </CardHeader>
          
          {/* Messages */}
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full p-4">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <img 
                    src={TACO_MASCOT_URL} 
                    alt="Taco" 
                    className="w-20 h-20 mx-auto mb-4 rounded-full"
                  />
                  <h3 className="text-lg font-medium text-zinc-700">I'm Taco, your ops assistant!</h3>
                  <p className="text-sm text-zinc-500 mt-1">
                    {isStaff && !isAdmin 
                      ? "Ask me about your schedule, cleaning tips, or app help."
                      : "I can help with bookings, staff assignments, and operational tasks."}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 mt-4">
                    {dashboard?.quick_actions?.slice(0, 3).map((action) => (
                      <Button
                        key={action.id}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickAction(action.prompt)}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={cn(
                        "flex gap-3",
                        msg.role === 'user' ? 'justify-end' : 'justify-start'
                      )}
                    >
                      {msg.role !== 'user' && (
                        <img 
                          src={TACO_MASCOT_URL} 
                          alt="Taco" 
                          className="w-8 h-8 rounded-full flex-shrink-0"
                        />
                      )}
                      <div
                        className={cn(
                          "max-w-[80%] rounded-lg p-3",
                          msg.role === 'user' 
                            ? 'bg-amber-500 text-white' 
                            : msg.type === 'error'
                            ? 'bg-red-50 text-red-800 border border-red-200'
                            : 'bg-zinc-100 text-zinc-800'
                        )}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        
                        {/* Tool call indicators */}
                        {msg.tool_calls?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-zinc-200">
                            <p className="text-xs text-zinc-500 mb-1">Actions taken:</p>
                            <div className="flex flex-wrap gap-1">
                              {msg.tool_calls.map((tc, idx) => (
                                <Badge 
                                  key={idx}
                                  variant={tc.success ? 'default' : 'destructive'}
                                  className="text-xs"
                                >
                                  {tc.success ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                                  {tc.name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <p className="text-xs opacity-60 mt-1">
                          {formatTime(msg.timestamp)}
                        </p>
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-amber-700 text-sm font-medium">
                            {user?.full_name?.[0] || 'U'}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
          </CardContent>

          {/* Input */}
          <div className="p-4 border-t">
            <form 
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="flex gap-2"
            >
              <Input
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Taco anything..."
                disabled={sending}
                className="flex-1"
                data-testid="taco-chat-input"
              />
              <Button 
                type="submit" 
                disabled={sending || !inputMessage.trim()}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="taco-send-btn"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        </Card>
      </div>
      
      {/* Phase 4: Action Confirmation Modal */}
      {confirmModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setConfirmModal(null)}
          data-testid="action-confirm-modal"
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">
              Confirm Action
            </h3>
            <p className="text-sm text-zinc-600 mb-4">
              {confirmModal.action.description}
            </p>
            
            <div className="bg-zinc-50 rounded p-3 mb-4">
              <p className="text-xs text-zinc-500 mb-1">This will execute:</p>
              <p className="text-sm font-medium text-zinc-800">
                {confirmModal.action.label}
              </p>
              {confirmModal.action.auto_resolve && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Will auto-resolve the alert if successful
                </p>
              )}
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setConfirmModal(null)}
                data-testid="action-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={() => executeAction(confirmModal.alertId, confirmModal.action)}
                disabled={executingAction !== null}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="action-confirm-btn"
              >
                {executingAction ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Confirm & Execute
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TacoV2Panel;
